import { Ionicons } from '@expo/vector-icons';
import React from "react";
import { View, Text, StyleSheet, Platform } from "react-native";
import { ViewProps } from 'react-native';

import { t } from "../../../i18n/translations";
import { iqesColors } from "../../../shared/ui/iqesDesignSystem";
import { useLanguageStore } from "../../quiz-core/languageStore";

import MultiplayerActionButton from "./MultiplayerActionButton";


interface RoomActionsProps extends ViewProps {
  isHost: boolean;
  playerIsReady: boolean; // Added
  canStartGame: boolean; // Renamed from allReady
  isStartingGame: boolean; // Renamed from loadingStart
  onToggleReady: () => void; // Added
  onStartGame: () => void;
  onInvite?: () => void; // Made optional
  onLeave?: () => void; // Made optional
  playerCount?: number; // Number of players in the room
}

const RoomActions = ({
  isHost,
  playerIsReady,
  canStartGame,
  isStartingGame,
  onToggleReady,
  onStartGame,
  onInvite,
  onLeave,
  playerCount = 0,
  ...rest
}: RoomActionsProps) => {
  const { language } = useLanguageStore();
  const showHostReadyButton = isHost && !playerIsReady;
  const showStartButton = isHost && playerIsReady;

  return (
    <View style={styles.footer} data-oid="sw3tjl:" {...rest}>
      <View style={styles.buttonRow} data-oid="0t_.23a">
        {onInvite && (
          <MultiplayerActionButton
            label={t('multiplayer_lobby.shareInvite', language)}
            onPress={onInvite}
            style={[
              styles.button,
              styles.secondaryButton,
              !onLeave && styles.fullWidthButton
            ]}
            variant="secondary"
            data-oid="wu4py.x"
          />
        )}
        {onLeave && (
          <MultiplayerActionButton
            label={t('multiplayer_lobby.leaveRoom', language)}
            onPress={onLeave}
            style={[
              styles.button,
              styles.dangerButton,
              !onInvite && styles.fullWidthButton
            ]}
            variant="secondary"
            data-oid="xv9bqq0"
          />
        )}
      </View>

      {!isHost && !showStartButton && (
        <MultiplayerActionButton
          label={playerIsReady ? t('multiplayer_lobby.cancelReady', language) : t('multiplayer_lobby.iAmReady', language)}
          onPress={onToggleReady}
          disabled={isStartingGame}
          style={[
            styles.button,
            playerIsReady ? styles.warningButton : styles.primaryButton,
            styles.fullWidthButton
          ]}
          variant={playerIsReady ? "secondary" : "primary"}
          data-oid="8aktsrk"
        />
      )}

      {showHostReadyButton && (
        <MultiplayerActionButton
          label={t('multiplayer_lobby.iAmReady', language)}
          onPress={onToggleReady}
          disabled={isStartingGame}
          style={[
            styles.button,
            styles.primaryButton,
            styles.fullWidthButton
          ]}
          variant="primary"
          data-oid="host-ready-button"
        />
      )}

      {showStartButton && (
        <View style={{ width: '100%' }}>
          <MultiplayerActionButton
            label={isStartingGame ? t('multiplayer_lobby.starting', language) : t('multiplayer_lobby.startGame', language)}
            onPress={onStartGame}
            disabled={!canStartGame || isStartingGame}
            style={[
              styles.startButton,
              (!canStartGame || isStartingGame) && styles.startButtonDisabled,
              styles.fullWidthButton
            ]}
            variant="primary"
            data-oid="0zi0x1g"
          />
          {!canStartGame && !isStartingGame && playerCount < 2 && (
            <View style={styles.waitingHint}>
              <Ionicons name="people-outline" size={16} color={iqesColors.mutedWarm} style={{ marginRight: 6 }} />
              <Text style={styles.waitingHintText}>
                {t('multiplayer_lobby.waitingForPlayers', language)}
              </Text>
            </View>
          )}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  footer: {
    minHeight: 80,
    paddingVertical: 24,
    paddingHorizontal: 20,
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    maxWidth: 520,
    alignSelf: 'center',
    marginBottom: 0,
    marginTop: 8,
    borderTopWidth: 0,
    borderRadius: 22,
    borderWidth: 1,
    backgroundColor: 'rgba(8, 10, 24, 0.9)',
    borderColor: 'rgba(246, 190, 58, 0.42)',
    shadowColor: iqesColors.gold,
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.16,
    shadowRadius: 14,
    elevation: 8,
    ...Platform.select({
      ios: {},
      android: {},
      web: {
        boxShadow: "0px -3px 4px rgba(29,26,35,0.06)"
      }
    }),
    zIndex: 10
  },
  buttonRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 12
  },
  button: {
    flex: 1,
    marginHorizontal: 4,
    paddingVertical: 14,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
    shadowColor: '#1D1A23',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 3,
    elevation: 2,
  },
  secondaryButton: {
    borderWidth: 1,
  },
  secondaryButtonText: {
    color: "#1D1A23",
    fontWeight: "700"
  },
  dangerButton: {
    backgroundColor: iqesColors.red,
  },
  dangerButtonText: {
    color: "#FFFFFF",
    fontWeight: "700",
  },
  startButton: {
    backgroundColor: iqesColors.green,
    paddingVertical: 16,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
    shadowColor: '#1D1A23',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 3,
    elevation: 2,
  },
  startButtonDisabled: {
    backgroundColor: "rgba(231, 222, 211, 0.22)",
    shadowOpacity: 0.03,
  },
  startButtonText: {
    color: "#FFFFFF",
    fontSize: 17,
    fontWeight: "700",
  },
  primaryButton: {
    backgroundColor: iqesColors.royalPurple,
  },
  primaryButtonText: {
    color: "#FFFFFF",
    fontWeight: "700",
    fontSize: 15,
  },
  warningButton: {
    backgroundColor: iqesColors.gold,
  },
  warningButtonText: {
    color: "#FFFFFF",
    fontWeight: "700",
    fontSize: 15,
  },
  fullWidthButton: {
    flexGrow: 1, // Allow button to grow if in a row
    flexShrink: 1,
    width: "100%", // Default to full width if it's the only button in its conceptual row
    marginHorizontal: 0, // No horizontal margin for full width
    marginTop: 8, // Margin for stacked buttons
  },
  waitingHint: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 10,
    paddingVertical: 4,
  },
  waitingHintText: {
    color: iqesColors.mutedWarm,
    fontSize: 14,
    fontWeight: '500',
  },
});

export default RoomActions;
